/**
 * Changelog — feed de novidades por deploy. O time acompanha e testa.
 * Admin publica/edita; demais só leem (entradas publicadas).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CheckCircle2, Pencil, Plus, Rocket, Trash2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  changelogApi,
  KIND_LABEL,
  KIND_OPTIONS,
  KIND_VARIANT,
  type ChangelogEntry,
  type ChangelogItem,
  type ChangelogKind,
} from "@/lib/api-changelog";
import { formatDateTime } from "@/lib/format";
import { useAuth } from "@/stores/auth";

function isRecent(iso: string): boolean {
  const diff = Date.now() - new Date(iso).getTime();
  return diff >= 0 && diff < 7 * 24 * 60 * 60 * 1000; // 7 dias
}

export function ChangelogPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ChangelogEntry | null>(null);
  const [creating, setCreating] = useState(false);

  const list = useQuery({ queryKey: ["changelog"], queryFn: changelogApi.list });

  const remove = useMutation({
    mutationFn: (id: string) => changelogApi.remove(id),
    onSuccess: () => {
      toast.success("Entrada removida");
      qc.invalidateQueries({ queryKey: ["changelog"] });
    },
  });

  const toggleTest = useMutation({
    mutationFn: ({ id, tested }: { id: string; tested: boolean }) =>
      tested ? changelogApi.unmarkTested(id) : changelogApi.markTested(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["changelog"] }),
  });

  return (
    <>
      <PageHeader
        title="Changelog"
        description="Novidades de cada deploy — acompanhe e teste"
        actions={
          isAdmin ? (
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> Nova entrada
            </Button>
          ) : undefined
        }
      />

      {list.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : (list.data?.length ?? 0) === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center dark:border-white/10">
          <Rocket className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-2 font-medium">Ainda sem novidades publicadas</p>
        </div>
      ) : (
        <div className="relative space-y-8 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-px before:bg-slate-200 dark:before:bg-white/10">
          {list.data?.map((entry) => (
            <article key={entry.id} className="relative pl-7">
              <span className="absolute left-0 top-1.5 h-3.5 w-3.5 rounded-full border-2 border-primary-500 bg-white dark:bg-card" />
              <div className="flex flex-wrap items-center gap-2">
                <time className="text-xs font-medium text-muted-foreground tabular-nums">
                  {formatDateTime(entry.published_at)}
                </time>
                {entry.version && (
                  <Badge variant={"muted" as never}>{entry.version}</Badge>
                )}
                {isRecent(entry.published_at) && (
                  <Badge variant={"success" as never} dot>
                    novo
                  </Badge>
                )}
                {!entry.is_published && (
                  <Badge variant={"warning" as never}>rascunho</Badge>
                )}
                {isAdmin && (
                  <div className="ml-auto flex gap-1">
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      onClick={() => setEditing(entry)}
                      aria-label="Editar"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
                      onClick={() => remove.mutate(entry.id)}
                      aria-label="Excluir"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
              <h3 className="mt-1 text-lg font-semibold tracking-tight">{entry.title}</h3>
              <ul className="mt-3 space-y-2">
                {entry.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <Badge variant={(KIND_VARIANT[it.kind] ?? "muted") as never}>
                      {KIND_LABEL[it.kind] ?? it.kind}
                    </Badge>
                    <span className="text-sm leading-relaxed">{it.text}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex items-center gap-3">
                <Button
                  size="sm"
                  variant={entry.tested_by_me ? "default" : "outline"}
                  onClick={() => toggleTest.mutate({ id: entry.id, tested: entry.tested_by_me })}
                  disabled={toggleTest.isPending}
                >
                  {entry.tested_by_me ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  {entry.tested_by_me ? "Você testou" : "Marcar como testado"}
                </Button>
                {entry.tested_count > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {entry.tested_count}{" "}
                    {entry.tested_count === 1 ? "pessoa testou" : "pessoas testaram"}
                  </span>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <EntryDialog
          entry={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EntryDialog({ entry, onClose }: { entry: ChangelogEntry | null; onClose: () => void }) {
  const qc = useQueryClient();
  const isEdit = !!entry;
  const [version, setVersion] = useState(entry?.version ?? "");
  const [title, setTitle] = useState(entry?.title ?? "");
  const [publishedAt, setPublishedAt] = useState(toLocalInput(entry?.published_at ?? null));
  const [items, setItems] = useState<ChangelogItem[]>(
    entry?.items?.length ? entry.items : [{ kind: "feature", text: "" }],
  );

  const setItem = (i: number, patch: Partial<ChangelogItem>) =>
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const addItem = () => setItems((prev) => [...prev, { kind: "feature", text: "" }]);
  const removeItem = (i: number) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const save = useMutation({
    mutationFn: () => {
      const cleanItems = items.filter((it) => it.text.trim()).map((it) => ({ ...it, text: it.text.trim() }));
      const body = {
        version: version.trim() || null,
        title: title.trim(),
        published_at: publishedAt ? new Date(publishedAt).toISOString() : null,
        items: cleanItems,
      };
      return isEdit ? changelogApi.update(entry.id, body) : changelogApi.create(body);
    },
    onSuccess: () => {
      toast.success(isEdit ? "Entrada atualizada" : "Entrada publicada");
      qc.invalidateQueries({ queryKey: ["changelog"] });
      onClose();
    },
    onError: () => toast.error("Falha ao salvar"),
  });

  const canSave = title.trim() && items.some((it) => it.text.trim());

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar entrada" : "Nova entrada do changelog"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="cl-version">Versão</Label>
              <Input
                id="cl-version"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="roadmap-v2"
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="cl-when">Data/hora (vazio = agora)</Label>
              <Input
                id="cl-when"
                type="datetime-local"
                value={publishedAt}
                onChange={(e) => setPublishedAt(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cl-title">Título</Label>
            <Input
              id="cl-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex.: Portais do cliente e do técnico"
            />
          </div>

          <div className="space-y-2">
            <Label>Novidades (ponto a ponto)</Label>
            {items.map((it, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="w-36 shrink-0">
                  <Select
                    value={it.kind}
                    onValueChange={(v) => setItem(i, { kind: v as ChangelogKind })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {KIND_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input
                  value={it.text}
                  onChange={(e) => setItem(i, { text: e.target.value })}
                  placeholder="O que mudou / o que testar"
                />
                <Button
                  size="icon-sm"
                  variant="ghost"
                  onClick={() => removeItem(i)}
                  disabled={items.length === 1}
                  aria-label="Remover item"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addItem}>
              <Plus className="h-4 w-4" /> Adicionar item
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => save.mutate()} disabled={!canSave || save.isPending}>
            {isEdit ? "Salvar" : "Publicar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
